import React, { useRef, useEffect, useState } from 'react';
import { useFontLoader } from '../../../../dummies/RichTextEditor/useFontLoader';
import { heritageColors } from '../../../../heritageTheme.ts';

const Timeline = ({ data = { notices: [] }, title = 'לוח מודעות', showTitle = true }) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const firstSetRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const activeNotices = (data.notices || []).filter(
    n => !n.expiryDate || new Date(n.expiryDate) > now
  );

  const allNoticesHtml = activeNotices.map(n => n.content).join(' ') || '';
  useFontLoader(allNoticesHtml);

  useEffect(() => {
    const checkScroll = () => {
      if (!containerRef.current || !firstSetRef.current || activeNotices.length === 0) return;
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = firstSetRef.current.offsetHeight;
      setNeedsScroll(containerHeight > 0 && contentHeight > containerHeight + 2);
    };
    const timer = setTimeout(checkScroll, 100);
    const resizeObserver = new ResizeObserver(checkScroll);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => { clearTimeout(timer); resizeObserver.disconnect(); };
  }, [activeNotices]);

  const dotColor = heritageColors.gold || '#D4AF37';
  const lineColor = heritageColors.border || '#D7CCC8';
  const navyColor = heritageColors.navy || '#1a3a5c';

  const renderItems = (notices, keyPrefix = '') =>
    notices.map((notice) => (
      <li
        key={`${keyPrefix}${notice.id}`}
        data-notice-item
        style={{ position: 'relative', paddingRight: '1.4em', paddingBottom: '1.2em' }}
      >
        {/* dot aligned to the top of the block, on the vertical line */}
        <span style={{
          position: 'absolute',
          right: '-0.3em',
          top: '0.3em',
          width: '0.55em',
          height: '0.55em',
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
          zIndex: 1,
        }} />
        {/* full-width content — alignment comes from the rich text itself */}
        <div
          style={{ fontSize: '0.75em', color: navyColor, lineHeight: 1.55, width: '100%' }}
          dangerouslySetInnerHTML={{ __html: notice.content }}
        />
      </li>
    ));

  return (
    <div style={{
      padding: '20px',
      direction: 'rtl',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      fontSize: 'inherit',
      boxSizing: 'border-box',
    }}>
      {showTitle && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: `0.0625em solid ${lineColor}`,
          paddingBottom: '8px',
          marginBottom: '16px',
        }}>
          <span className="material-symbols-outlined" style={{ color: navyColor, fontSize: '1.1em' }}>campaign</span>
          <h3 style={{ margin: 0, fontWeight: 'bold', color: navyColor, fontSize: '0.875em' }}>{title}</h3>
        </div>
      )}

      {activeNotices.length === 0 ? (
        <p style={{ color: heritageColors.slate || '#795548', fontSize: '0.875em' }}>אין מודעות להצגה</p>
      ) : (
        <div ref={containerRef} style={{ overflow: 'hidden', flex: 1, position: 'relative' }}>
          {needsScroll && (
            <style>{`
              @keyframes scrollNoticesTimeline {
                0% { transform: translateY(0); }
                100% { transform: translateY(calc(-100% / 3)); }
              }
            `}</style>
          )}
          <div ref={contentRef} style={{
            animation: needsScroll ? 'scrollNoticesTimeline 30s linear infinite' : 'none',
          }}>
            {/* vertical spine — runs the height of the list */}
            <div ref={firstSetRef} style={{ position: 'relative', paddingRight: '0.6em' }}>
              <div style={{
                position: 'absolute',
                right: '0',
                top: '0.55em',
                bottom: 0,
                width: '1px',
                backgroundColor: lineColor,
              }} />
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {renderItems(activeNotices)}
              </ul>
            </div>

            {needsScroll && (
              <>
                <div style={{ position: 'relative', paddingRight: '0.6em' }}>
                  <div style={{ position: 'absolute', right: '0', top: '0.55em', bottom: 0, width: '1px', backgroundColor: lineColor }} />
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {renderItems(activeNotices, 'dup1-')}
                  </ul>
                </div>
                <div style={{ position: 'relative', paddingRight: '0.6em' }}>
                  <div style={{ position: 'absolute', right: '0', top: '0.55em', bottom: 0, width: '1px', backgroundColor: lineColor }} />
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {renderItems(activeNotices, 'dup2-')}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Timeline;
